<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
    getApproverGrant, approverGroupSites,
    approveGrant, rejectGrant, renewGrant, revokeGrant,
    grantErrorMessage,
} from '@/api/approver';
import type { ApproverGrant, GroupSitesPreview, GrantStatus } from '@/api/types';
import { formatDate } from '@/lib/datetime';

const props = defineProps<{ grantId: string }>();
const router = useRouter();

const grant = ref<ApproverGrant | null>(null);
const sites = ref<GroupSitesPreview | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);

const acting = ref(false);
const actionError = ref<string | null>(null);

function isoDate(d: Date): string { return d.toISOString().slice(0, 10); }
const now = new Date();
const minDate = isoDate(new Date(now.getTime() + 24 * 3600 * 1000));
const maxDate = isoDate(new Date(now.getTime() + 365 * 24 * 3600 * 1000));
const expiry = ref(maxDate);

function setMonths(months: number) {
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    expiry.value = isoDate(d);
}

// The native date input's :min/:max only guards the picker — a manually typed
// value still updates v-model, and the approve/renew buttons aren't form-submit
// so browser constraint validation never fires. Guard explicitly: expiry must
// be in the future and ≤ 1 year ahead. (YYYY-MM-DD compares lexically =
// chronologically.) Mirrors the server-side validateExpiry.
const expiryValid = computed(() => !!expiry.value && expiry.value >= minDate && expiry.value <= maxDate);

async function load() {
    loading.value = true;
    error.value = null;
    try {
        grant.value = await getApproverGrant(props.grantId);
        sites.value = null;
        if (grant.value) {
            try {
                sites.value = await approverGroupSites(grant.value.groupId);
            } catch { /* preview is best-effort */ }
        }
    } catch (e) {
        error.value = grantErrorMessage(e);
    } finally {
        loading.value = false;
    }
}

onMounted(load);

function expiryIso(): string { return new Date(expiry.value).toISOString(); }

// Readable, locale-formatted echo of the picked expiry date, shown next to the
// date input so the chosen day is unambiguous regardless of the browser's
// native date-input format (which can render MM/DD/YYYY on a US-locale browser).
const expiryReadable = computed(() => formatDate(expiryIso()));

async function run(fn: () => Promise<unknown>, back = false) {
    acting.value = true;
    actionError.value = null;
    try {
        await fn();
        if (back) router.push('/approvals');
        else await load();
    } catch (e) {
        actionError.value = grantErrorMessage(e);
    } finally {
        acting.value = false;
    }
}

function approve() { if (!expiryValid.value) return; run(() => approveGrant(props.grantId, expiryIso()), true); }
function renew() { if (!expiryValid.value) return; run(() => renewGrant(props.grantId, expiryIso())); }
function revoke() {
    if (!confirm('Revoke this access now? The applicant loses the group on their next sign-in.')) return;
    run(() => revokeGrant(props.grantId), true);
}
function reject() {
    const reason = prompt('Reason for declining (optional):') ?? undefined;
    run(() => rejectGrant(props.grantId, reason || undefined), true);
}

function fmtDay(d?: string): string { return d ? formatDate(d) : '—'; }

const STATUS_CLASS: Record<GrantStatus, string> = {
    pending: 'bg-amber-900/40 text-amber-200',
    approved: 'bg-green-900/40 text-green-300',
    expired: 'bg-bp-panel-2 text-bp-muted',
    revoked: 'bg-bp-panel-2 text-bp-muted',
    rejected: 'bg-red-900/40 text-red-300',
};
</script>

<template>
    <div class="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <RouterLink to="/approvals" class="text-bp-muted hover:text-bp-text text-sm">← Back to approvals</RouterLink>

        <div v-if="loading" class="mt-4 text-sm text-bp-muted">Loading…</div>
        <div v-else-if="error" class="mt-4 bg-red-900/30 border border-red-700 text-red-200 px-4 py-2 rounded text-sm">
            {{ error }}
        </div>

        <template v-else-if="grant">
            <div class="flex items-center gap-3 mt-3 mb-5">
                <h1 class="text-2xl font-semibold">{{ grant.user?.displayName || grant.user?.email || 'Access request' }}</h1>
                <span class="px-2 py-0.5 rounded text-xs font-medium" :class="STATUS_CLASS[grant.status]">{{ grant.status }}</span>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section class="bg-bp-panel border border-bp-border rounded p-4">
                    <h2 class="font-semibold text-sm mb-3">Request</h2>
                    <dl class="text-sm space-y-2">
                        <div class="flex justify-between"><dt class="text-bp-muted">Email</dt><dd class="text-bp-text">{{ grant.user?.email || '—' }}</dd></div>
                        <div class="flex justify-between"><dt class="text-bp-muted">Company</dt><dd class="text-bp-text">{{ grant.user?.company || '—' }}</dd></div>
                        <div class="flex justify-between"><dt class="text-bp-muted">Phone</dt><dd class="text-bp-text">{{ grant.user?.phone || '—' }}</dd></div>
                        <div class="flex justify-between"><dt class="text-bp-muted">Position</dt><dd class="text-bp-text">{{ grant.user?.position || '—' }}</dd></div>
                        <div class="flex justify-between"><dt class="text-bp-muted">Group</dt><dd class="text-bp-text">{{ grant.group?.name || '—' }}</dd></div>
                        <div class="flex justify-between"><dt class="text-bp-muted">Requested</dt><dd class="text-bp-text">{{ fmtDay(grant.createdAt) }}</dd></div>
                        <div class="flex justify-between"><dt class="text-bp-muted">Expires</dt><dd class="text-bp-text">{{ fmtDay(grant.expiresAt) }}</dd></div>
                        <div v-if="grant.approvedBy" class="flex justify-between"><dt class="text-bp-muted">Approved by</dt><dd class="text-bp-text">{{ grant.approvedBy }}</dd></div>
                    </dl>

                    <div class="border-t border-bp-border mt-4 pt-4">
                        <div v-if="actionError" class="bg-red-900/30 border border-red-700 text-red-200 px-3 py-2 rounded mb-3 text-sm">
                            {{ actionError }}
                        </div>

                        <template v-if="grant.status === 'pending' || grant.status === 'approved' || grant.status === 'expired'">
                            <label class="block text-sm text-bp-muted mb-1">Expiry date (max 1 year ahead)</label>
                            <input type="date" v-model="expiry" :min="minDate" :max="maxDate"
                                   class="w-full px-3 py-2 bg-bp-bg border border-bp-border rounded text-sm mb-2" />
                            <div class="flex flex-wrap gap-2 mb-3">
                                <button type="button" class="px-2 py-1 rounded border border-bp-border text-bp-muted hover:text-bp-text text-xs" @click="setMonths(3)">+3 months</button>
                                <button type="button" class="px-2 py-1 rounded border border-bp-border text-bp-muted hover:text-bp-text text-xs" @click="setMonths(6)">+6 months</button>
                                <button type="button" class="px-2 py-1 rounded border border-bp-border text-bp-muted hover:text-bp-text text-xs" @click="setMonths(12)">+1 year</button>
                            </div>
                            <p v-if="expiryValid" class="text-xs text-bp-muted mb-3">
                                Access will expire on <span class="text-bp-text font-medium">{{ expiryReadable }}</span> (UTC).
                            </p>
                            <p v-else class="text-xs text-red-400 mb-3">
                                Pick a date in the future, at most one year ahead.
                            </p>
                            <div class="flex flex-wrap gap-2 justify-end">
                                <button v-if="grant.status === 'pending'" type="button" :disabled="acting"
                                        class="px-3 py-1.5 rounded text-bp-muted hover:text-bp-text text-sm disabled:opacity-50" @click="reject">
                                    Decline
                                </button>
                                <button v-if="grant.status === 'approved'" type="button" :disabled="acting"
                                        class="px-3 py-1.5 rounded text-red-400 hover:text-red-300 text-sm disabled:opacity-50" @click="revoke">
                                    Revoke
                                </button>
                                <button type="button" :disabled="acting || !expiryValid"
                                        class="px-3 py-1.5 rounded bg-brand-bright-blue text-white text-sm hover:bg-brand-bright-blue/90 disabled:opacity-50"
                                        @click="grant.status === 'pending' ? approve() : renew()">
                                    {{ acting ? 'Working…' : (grant.status === 'pending' ? 'Approve' : grant.status === 'expired' ? 'Re-activate' : 'Renew') }}
                                </button>
                            </div>
                        </template>
                        <p v-else class="text-bp-muted text-sm">This request is {{ grant.status }} — no further action.</p>
                    </div>
                </section>

                <section class="bg-bp-panel border border-bp-border rounded p-4">
                    <h2 class="font-semibold text-sm mb-3">Sites this grants access to</h2>
                    <p v-if="sites && sites.sites.length === 0" class="text-bp-muted text-sm">
                        No sites are reachable through this group yet.
                    </p>
                    <ul v-else-if="sites" class="text-sm divide-y divide-bp-border">
                        <li v-for="s in sites.sites" :key="s.id" class="py-1.5 text-bp-text">{{ s.name }}</li>
                    </ul>
                    <p v-else class="text-bp-muted text-sm">Loading…</p>
                </section>
            </div>
        </template>
    </div>
</template>
